import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosResponse } from 'axios';

import PaymentModal from 'components/payment/payment-modal/payment-modal';
import { ReactComponent as CoinIcon } from 'assets/icons/coin_icon.svg';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar_icon.svg';
import { ReactComponent as Calendar2Icon } from 'assets/icons/calendar2_icon.svg';
import { ReactComponent as CardIcon } from 'assets/icons/card_icon.svg';

// Data
import paymentPlanData from 'components/payment/payment-plans.json';
import PopupFrame from 'layouts/PopupFrame';
import Button from 'common/button/button';
import Label from 'common/label/label';
import { StatusType, useSubscribeStatus } from 'hooks/useSubscribe';
import useLoadingStore from 'zustands/useLoadingStore';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';
import moment from 'moment';
import { addComma } from 'constants/utils';
import { useUserStore } from '../../zustands/useUserStore';
import { ISubscriptionDetailList, ISubscriptionItem } from '~/types/payment';
import { formatDate } from '@fullcalendar/core';

const PaymentInfo: React.FC = () => {
  const navigate = useNavigate();

  const { setLoadingState } = useLoadingStore();

  const { appApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [paymentModal, setPaymentModal] = React.useState<boolean>(false);
  const [paymentList, setPaymentList] = React.useState<ISubscriptionDetailList[] | null>(null);
  const [subscriptionItem, setSubscriptionItem] = React.useState<ISubscriptionItem | null>(null);

  const userSeq = useUserStore((state) => state.userSeq);

  const fetchSubscriptionInfo = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await appApiNode('get', `/subscription/subscriptionList?m_no=${userSeq}`);
      if (data.result_code !== 'SUCCESS' || data.result_msg === 'NO_DATA' || !data.result_data) {
        setSubscriptionItem(null);
        return;
      }
      setSubscriptionItem({ ...data.result_data[0] });
    } catch (err) {
      checkErrorLog(err, `/subscription/subscriptionList?m_no=${userSeq}`);
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  const fetchPayInfo = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await appApiNode(
        'get',
        `/subscription/subscriptionDetail?m_no=${userSeq}`,
      );
      if (data.result_code !== 'SUCCESS' || data.result_msg === '구독 상세내역이 없습니다.' || !data.result_data) {
        setPaymentList(null);
        return;
      }
      console.log('data.result_data', data.result_data);
      setPaymentList(data.result_data.list);
    } catch (err) {
      checkErrorLog(err, `/subscription/subscriptionDetail?m_no=${userSeq}`);
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  const toggleModal = (): any => {
    setPaymentModal(!paymentModal);
  };

  React.useEffect(() => {
    fetchPayInfo();
    fetchSubscriptionInfo();
  }, []);

  return (
    <PopupFrame>
      <div className="flex flex-col items-center">
        <div className="flex flex-col gap-[44px] pt-10">
          <h1 className="text-[30px] font-bold text-gray-2">결제관리</h1>
          {subscriptionItem ? (
            <div className="flex w-[600px] flex-col rounded bg-white px-[48px] py-[60px] shadow-[2px_2px_20px_0px_#0000000F]">
              <p className="m-0 w-fit rounded-sm bg-[#E5F4F2] px-2 py-1 text-[14px] text-[#40B09E]">
                {subscriptionItem.rss_status === 'ACTIVE' && '이용중'}
                {subscriptionItem.rss_status === 'INACTIVE' && '비활성'}
                {subscriptionItem.rss_status === 'CANCEL' && '취소·환불'}
                {subscriptionItem.rss_status === 'CANCEL_WAIT' && '해지'}
              </p>

              <h2 className="m-0 pt-[28px] text-[22px] font-bold text-gray-2">{subscriptionItem.pp_name}</h2>

              <div className="grid grid-cols-[25px_160px_1fr] items-center gap-2 pt-[32px]">
                <CalendarIcon className="size-[28px]" />
                <p className="m-0 text-[16px] font-medium leading-none text-gray-5">구독 기간</p>
                <p className="m-0 text-[16px] font-bold leading-none text-gray-3">
                  {subscriptionItem.rss_start_date} ~ {subscriptionItem.rss_end_date}
                </p>
              </div>
              {subscriptionItem.rss_status === 'CANCEL_WAIT' ? (
                <></>
              ) : (
                <div className="grid grid-cols-[25px_160px_1fr] items-center gap-2 pt-[14px]">
                  <Calendar2Icon className="size-[28px]" />
                  <p className="m-0 text-[16px] font-medium leading-none text-gray-5">다음 결제일</p>
                  <p className="m-0 text-[16px] font-bold leading-none text-gray-3">{subscriptionItem.rss_next_date}</p>
                </div>
              )}

              <p className="pt-[20px] text-[14px] font-normal text-gray-3">
                *자동결제 해지는 샵솔 앱에서 설정할 수 있어요.
                <br />
                [샵솔 앱 로그인 &gt; 설정 &gt; 구독관리 &gt; 자동결제 해지]
              </p>
            </div>
          ) : (
            <div className="flex w-[600px] flex-col items-center justify-center rounded bg-white px-[48px] py-[80px] shadow-[2px_2px_20px_0px_#0000000F]">
              <p className="text-[18px] font-semibold text-gray-3">이용 중인 구독이 없습니다</p>
              <p className="pt-[8px] text-[14px] text-gray-4">샵솔 앱에서 구독을 시작해보세요.</p>
            </div>
          )}

          <div className="flex w-[600px] flex-col rounded bg-white px-[48px] py-[60px] shadow-[2px_2px_20px_0px_#0000000F]">
            <h2 className="m-0 pb-[32px] text-[22px] font-bold text-gray-2">결제 내역</h2>
            {paymentList ? (
              <>
                {paymentList.map((item) => (
                  <div key={item.ps_sub_id} className="gap-[15px] border-t border-[#868686] py-[35px]">
                    <div className="grid grid-cols-[160px_1fr] items-center">
                      <p className="text-[15px] font-normal text-[#262626]">결제 날짜</p>
                      <p className="text-right text-[15px] font-medium text-[#262626]">
                        {moment(item.payment_date).format('YYYY년MM월DD일')}
                      </p>
                    </div>
                    <div className="grid grid-cols-[160px_1fr] items-center">
                      <p className="text-[15px] font-normal text-[#262626]">구독 기간</p>
                      <p className="text-right text-[15px] font-medium text-[#262626]">
                        {moment(item.period_start).format('YYYY년MM월DD일')} ~{' '}
                        {moment(item.period_end).format('YYYY년MM월DD일')}
                      </p>
                    </div>
                    <div className="grid grid-cols-[160px_1fr] items-center">
                      <p className="text-[15px] font-normal text-[#262626]">금액</p>
                      <p className="text-right text-[15px] font-medium text-[#262626]">{addComma(item.amount)}원</p>
                    </div>
                    <div className="grid grid-cols-[160px_1fr] items-center">
                      <p className="text-[15px] font-normal text-[#262626]">부가세(10%)</p>
                      <p className="text-right text-[15px] font-medium text-[#262626]">{addComma(item.tax)}원</p>
                    </div>
                    <div className="grid grid-cols-[160px_1fr] items-center">
                      <p className="text-[15px] font-normal text-[#262626]">결제 수단</p>
                      <p className="text-right text-[15px] font-medium text-[#262626]">{item.payment_method}</p>
                    </div>
                    <div className="grid grid-cols-[160px_1fr] items-center">
                      <p className="text-[15px] font-normal text-[#262626]">총 합계</p>
                      <p className="text-right text-[15px] font-semibold text-[#262626]">
                        {addComma(item.total_amount)}원
                      </p>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center px-[48px] py-[80px]">
                <p className="text-[18px] text-gray-3">결제 내역이 없습니다.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </PopupFrame>
  );
};
export default PaymentInfo;
